import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Spacing, Dimensions } from '@/shared/theme';
import { Button, Text } from '@/shared/ui';
import { ResultTitle, ResultBody } from '@/shared/resultText';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import {
  VIEW_CHECK_SMS,
  CHECK_SMS_TRY_AGAIN,
  NEED_HELP,
} from '@/consts/events';

import smsLottie from './assets/smsLottie';

// Workaround for react-lottie TypeScript issue
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LottieComponent = Lottie as any;

// A POST_REGISTRATION_PATHS route (Layout.tsx): a plain, unframed, normal
// page-scroll screen with no rail/phase-bar to anchor to. min-height +
// justify-content:center keep this vertically centered (Boris `.rg-result`),
// matching the other terminal screens (not-eligible / cap-reached /
// email-sent / eligibility-result). The previous fixed-`height` +
// `space-between` layout stranded the title block near the top and the CTA
// block at the very bottom with a large dead gap between them; this was
// never caught earlier because this screen requires a live signup + SMS-send
// round-trip to reach (RND-35692 Phase 4 tooling gap), not seedable like most
// other screens.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
  gap: ${Spacing.x3}px;
  padding: ${Spacing.x4}px ${Spacing.x2}px;
`;

// Uses the shared Boris result-screen title scale (rg-result-h), consistent with
// not-eligible / eligibility-result / cap-reached / email-sent.
const StyledTitle = styled(ResultTitle)`
  margin-bottom: ${Spacing.x1_5}px;
`;

const StyledLottie = styled.div``;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;
  max-width: 400px;
`;

const StyledDisclaimer = styled(Text)`
  margin-bottom: ${Spacing.x1}px;
`;

const CTAButton = styled(Button)`
  width: 144px;
`;

const CheckSMSStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackView, track } = useAnalytics();
  const { pathname } = useLocation();

  // Integrate useAppFlow for flow management
  useAppFlow(STEP_KEYS.CHECK_SMS);

  useEffect(() => {
    trackView({ event: VIEW_CHECK_SMS });
  }, []);

  const handleSupportClick = () => {
    track({ event: NEED_HELP, properties: { screen: pathname } });
    // Dispatch custom event to open support modal in Header
    window.dispatchEvent(new CustomEvent('openSupportModal'));
  };

  const goToQRPage = () => {
    track({ event: CHECK_SMS_TRY_AGAIN });
    navigate('/download-app-qr');
  };

  const animationData = smsLottie(t('check-sms.lottie-text'));

  return (
    <Container>
      <StyledLottie aria-hidden="true">
        <LottieComponent
          width={304}
          height={250}
          options={{
            loop: false,
            autoplay: true,
            animationData,
          }}
        />
      </StyledLottie>
      <ContentWrapper>
        <StyledTitle size="h2" as="h1" centered>
          {t('check-sms.title')}
        </StyledTitle>
        <ResultBody size="p1" centered>
          {t('check-sms.description')}
        </ResultBody>
      </ContentWrapper>
      <ActionsWrapper>
        <StyledDisclaimer size="p2" centered>
          {t('check-sms.disclaimer')}
        </StyledDisclaimer>
        <CTAButton
          variant="action-primary"
          size="xsmall"
          onClick={goToQRPage}
          text={t('check-sms.try-again')}
        />
        <Button
          variant="action-tertiary"
          size="xsmall"
          text={t('general.contact-support')}
          onClick={handleSupportClick}
        />
      </ActionsWrapper>
    </Container>
  );
};

export { CheckSMSStep };
